import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { GroupListItemDataFragment, SubgroupListItemDataFragment } from '../GroupListing/GroupList.api';
import DetailCard from '../shared/DetailCard';
import ViewCourse from '../shared/ViewCourses';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import Tab from '@/components/common/Tab';
import type { TabItem } from '@/components/common/Tab/makeUseTabs';
import makeUseTabs from '@/components/common/Tab/makeUseTabs';

export enum ViewSubgroupTabKeys {
    GeneralInfo = '1',
    Courses = '2',
}

interface ViewSubgroupProps {
    open: boolean;
    group?: GroupListItemDataFragment;
    subgroup?: SubgroupListItemDataFragment;
    onClose: () => void;
}

const getTabsConfig = (
    t: (key: string) => string
): {
    key: string;
    name: string;
    component: React.FC<{
        group?: GroupListItemDataFragment;
        subgroup?: SubgroupListItemDataFragment;
    }>;
}[] => [
    { key: ViewSubgroupTabKeys.GeneralInfo, name: t('viewGroupModal.tabs.generalInfo'), component: DetailCard },
    { key: ViewSubgroupTabKeys.Courses, name: t('viewGroupModal.tabs.courses'), component: ViewCourse },
];

const getTabsForHook = (t: (key: string) => string): TabItem[] => {
    return getTabsConfig(t).map(tab => ({ key: tab.key, name: tab.name }));
};

const useViewGroupTabs = makeUseTabs(getTabsForHook);

const ViewSubgroup = ({ open, onClose, group, subgroup }: ViewSubgroupProps) => {
    const { t } = useTranslation('groups');

    const { tabs, activeTab, onTabChange } = useViewGroupTabs(t);

    useEffect(() => {
        if (open) {
            onTabChange(ViewSubgroupTabKeys.GeneralInfo);
        }
    }, [open, group, subgroup, onTabChange]);

    const tabsConfig = useMemo(() => getTabsConfig(t), [t]);

    const ActiveTabComponent = useMemo(() => {
        return tabsConfig.find(tab => tab.key === activeTab)?.component;
    }, [activeTab, tabsConfig]);

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                title={t('viewSubgroupModal.title')}
                className="w-full sm:max-w-3xl sm:min-w-[600px]"
                footer={
                    <div className="mt-5 border-t border-neutral-300 pt-4 sm:mt-4 sm:flex sm:flex-row sm:items-center sm:justify-end">
                        <div className="flex gap-2">
                            <Button onClick={onClose} outline>
                                {t('viewSubgroupModal.close')}
                            </Button>
                        </div>
                    </div>
                }
            >
                {group && (
                    <>
                        <div className="mb-4 overflow-x-auto border-b border-neutral-300">
                            <Tab tabs={tabs} onChange={onTabChange} />
                        </div>
                        <div className="max-h-[60vh] min-h-[300px] overflow-y-auto px-1 pb-2">
                            {ActiveTabComponent ? (
                                <ActiveTabComponent group={group} subgroup={subgroup} />
                            ) : (
                                <p>{t('selectTab')}</p>
                            )}
                        </div>
                    </>
                )}
            </Modal>
        </>
    );
};

export default ViewSubgroup;
